'use client';

/** INVESTORS — VCTronics pipeline, /api/vctronics/investors (738 live). */
import { useEffect, useMemo, useState } from 'react';
import { Glass, Pill, StatusChip, HoloTable, Row, mono, display } from '@/future/primitives';

const TYPE_FILTERS = ['ALL', 'Venture Capital', 'Accelerator/Incubator', 'Angel (individual)', 'Angel Group', 'Corporate Venture Capital'];
const STATUS_KIND: Record<string, 'good' | 'warn' | 'bad' | 'info' | 'dim'> = {
  replied: 'good', meeting: 'good', viewed_pitch: 'warn', emailed: 'info', passed: 'bad', not_contacted: 'dim',
};

export function InvestorsSurface() {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('ALL');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/vctronics/investors').then(r => r.json())
      .then(d => setRows(Array.isArray(d) ? d : d.investors || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter(r =>
    (type === 'ALL' || r.type === type) &&
    (!q || `${r.name} ${r.contact} ${r.email} ${r.sector_focus}`.toLowerCase().includes(q.toLowerCase()))
  ), [rows, q, type]);

  return (
    <div className="max-w-[1240px] mx-auto space-y-4" style={{ ['--uif-cols' as any]: '1.4fr 1.4fr 1fr .7fr .9fr 1.6fr' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <input placeholder="SEARCH INVESTORS…" value={q} onChange={e => setQ(e.target.value)}
          className="px-3 py-1.5 rounded-full bg-transparent outline-none w-60"
          style={{ ...mono(16, '.14em'), color: 'var(--uif-t2)', border: '1px solid rgba(125,232,255,.25)' }} />
        {TYPE_FILTERS.map(t => <Pill key={t} label={t === 'ALL' ? 'ALL' : t.split(' ')[0].toUpperCase()} active={type === t} onClick={() => setType(t)} />)}
        <span className="ml-auto" style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{filtered.length} / {rows.length}</span>
        <a href="https://pitch.vctronics.com/admin/outreach" target="_blank" rel="noopener noreferrer"
          style={{ ...mono(15, '.2em'), color: 'var(--uif-cy-interactive)', border: '1px solid rgba(125,232,255,.25)', borderRadius: 999, padding: '5px 12px', textDecoration: 'none' }}>ADMIN ↗</a>
      </div>
      <Glass className="uif-scanlines relative overflow-hidden" style={{ borderRadius: 'var(--uif-radius)' }}>
        <div className="py-3">
          <HoloTable head={['NAME', 'CONTACT', 'TYPE', 'FIT', 'STATUS', 'SECTOR FOCUS']}>
            {filtered.slice(0, 120).map(r => (
              <Row key={r.id} cols={6}>
                <div style={{ ...display(26, 400), color: 'var(--uif-t1)' }}>{r.name}</div>
                <div>
                  <div style={{ ...display(23, 300), color: 'var(--uif-t3)' }}>{r.contact || '—'}</div>
                  {r.email && <div style={{ ...mono(14), color: 'var(--uif-t6)' }}>{r.email}</div>}
                </div>
                <div style={{ ...mono(15, '.08em'), color: 'var(--uif-t4)' }}>{r.type || '—'}</div>
                <div style={{ ...mono(15), color: r.fit === 'Fitting' ? 'var(--uif-good)' : r.fit === 'Maybe' ? 'var(--uif-warn)' : 'var(--uif-t5)' }}>{r.fit || '—'}</div>
                <div><StatusChip label={(r.outreach_status || 'not_contacted').replace('_', ' ').toUpperCase()} kind={STATUS_KIND[r.outreach_status] || 'dim'} /></div>
                <div className="truncate" style={{ ...mono(15, '.04em'), color: 'var(--uif-t5)' }}>{r.sector_focus || '—'}</div>
              </Row>
            ))}
          </HoloTable>
          {loading && <div className="py-12 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>LOADING 700+ INVESTORS…</div>}
        </div>
      </Glass>
    </div>
  );
}
